import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import Container from '../components/Layout/Container';
import DashboardDateRangeWorktypeSelect from '../components/Dashboard/DashboardDateRangeWorktypeSelect';
import UserContext from '../store/User/UserContext';
import DashboardTEF from '../components/Dashboard/DashboardTEF';
import DashboardOSP from '../components/Dashboard/DashboardOSP';
import { useCookieWorktype, useWorktypes } from '../hooks';
import { appTimezone, budgetEnabled } from '../config';
import dayjs from '@utils/dayjs';

function Dashboard() {
    const loggedInUser = useContext(UserContext);
    const navigate = useNavigate();

    // isTEFUser already covers WFM (folded in by getUserInfo); WTM is its own role.
    const hasNoDashboardRole = !loggedInUser?.isOSPUser && !loggedInUser?.isTEFUser && !loggedInUser?.isWTMUser;

    // WTM is a read-mostly role: it does not get TEF capabilities (isTEFUser excludes
    // it) but should still see the read-only TEF dashboard, with the same broad scope.
    const seesTEFDashboard = Boolean(loggedInUser?.isTEFUser || loggedInUser?.isWTMUser);

    // With the Budget module disabled, a budget role must not block the
    // forecast/admin redirects (or trigger the /budget one).
    const isBudgetUser = budgetEnabled && loggedInUser?.isBudgetUser;
    const isBudgetOnly = isBudgetUser && hasNoDashboardRole && !loggedInUser?.isForecastUser;
    const isForecastOnly = loggedInUser?.isForecastUser && hasNoDashboardRole && !isBudgetUser;
    const isAdminOnly = loggedInUser?.isAdmin && hasNoDashboardRole && !isBudgetUser && !loggedInUser?.isForecastUser;

    useEffect(() => {
        if (isBudgetOnly) {
            navigate('/budget', { replace: true });
        } else if (isForecastOnly) {
            navigate('/forecasts', { replace: true });
        } else if (isAdminOnly) {
            navigate('/apiKeys', { replace: true });
        }
    }, [isBudgetOnly, isForecastOnly, isAdminOnly, navigate]);

    const [selectedStartDate, setSelectedStartDate] = useState(dayjs().tz(appTimezone).startOf('week').add(1, 'day'));
    const [selectedEndDate, setSelectedEndDate] = useState(dayjs().tz(appTimezone).endOf('week').add(1, 'day'));

    const [worktypeOptions] = useWorktypes({
        location: seesTEFDashboard ? null : loggedInUser?.primaryLocation?.id,
        prependAllWorktypesOption: seesTEFDashboard,
    });
    const [selectedWorktype, setSelectedWorktype] = useCookieWorktype(worktypeOptions);

    const handleWorktypeChange = (newValue) => {
        setSelectedWorktype(newValue);
    };

    const diffBetweenDateRangeInMs = selectedEndDate.diff(selectedStartDate);
    const diffBetweenDateRangeInDays = diffBetweenDateRangeInMs / (1000 * 3600 * 24);
    const showSlotIntervalOnChart = diffBetweenDateRangeInDays < 5;

    return loggedInUser?.isCallmartUser ? (
        <Container>
            <Grid container spacing={1} alignItems="stretch">
                <Grid item xs={12} sm={12} md={3} lg={2} sx={{ minWidth: '350px' }}>
                    <DashboardDateRangeWorktypeSelect
                        selectedStartDate={selectedStartDate}
                        selectedEndDate={selectedEndDate}
                        onStartDateChange={setSelectedStartDate}
                        onEndDateChange={setSelectedEndDate}
                        worktype={selectedWorktype}
                        worktypeOptions={worktypeOptions}
                        onWorktypeChange={handleWorktypeChange}
                    ></DashboardDateRangeWorktypeSelect>
                </Grid>
                {loggedInUser && loggedInUser.isOSPUser && (
                    <DashboardOSP
                        dateFrom={selectedStartDate}
                        dateTo={selectedEndDate}
                        worktype={selectedWorktype}
                        worktypes={worktypeOptions}
                        loggedInUser={loggedInUser}
                        showSlotIntervalOnChart={showSlotIntervalOnChart}
                    ></DashboardOSP>
                )}
                {loggedInUser && seesTEFDashboard && (
                    <DashboardTEF
                        dateFrom={selectedStartDate}
                        dateTo={selectedEndDate}
                        worktype={selectedWorktype}
                        showSlotIntervalOnChart={showSlotIntervalOnChart}
                    ></DashboardTEF>
                )}
            </Grid>
        </Container>
    ) : null;
}

export default Dashboard;
